import React from 'react';

import { MONTHLY_PLAN_IDS } from '@/constants/plans';
import { WebSubscriptionPlanType } from '@/modules/subscriptions';

import { useUserStore } from './useAccount';
import { useQueryStripeV2Prices } from './useQueryStripeV2Prices.ts';

const DEFAULT_PLANS: WebSubscriptionPlanType[] = [
  'pro.1w',
  'pro.1m',
  'pro.3m',
  'pro.1y',
];

const REBILL_PLANS: WebSubscriptionPlanType[] = [
  'pro.rebill_1w_discount',
  'pro.rebill_1m_discount',
  'pro.rebill_3m_discount',
];

const ONE_DAY_VIP_PLANS: WebSubscriptionPlanType[] = ['pro.one_off_1d_14_99'];

export const useDevmodeStripeV2Plans = () => {
  const selectedPlanId = useUserStore((store) => store.selectedPlanId);
  const setSelectedPlanId = useUserStore((store) => store.setSelectedPlanId);
  const user = useUserStore((store) => store.user);

  const subscriptionSet = user?.onboarding_data?.subscription_set;

  let plansToFetch: WebSubscriptionPlanType[];
  let defaultPlan: WebSubscriptionPlanType;

  if (subscriptionSet === 'rebill') {
    plansToFetch = REBILL_PLANS;
    defaultPlan = 'pro.rebill_1m_discount';
  } else if (subscriptionSet === 'one_day_vip') {
    plansToFetch = ONE_DAY_VIP_PLANS;
    defaultPlan = 'pro.one_off_1d_14_99';
  } else {
    plansToFetch = DEFAULT_PLANS;
    defaultPlan = 'pro.1m';
  }

  const { subscriptions, coins, isLoading, isSuccess, billingPortalLink } =
    useQueryStripeV2Prices({
      subscriptions: plansToFetch,
      coins: [],
    });

  const sortedSubscriptions = subscriptions.sort((a, b) => {
    const indexA = plansToFetch.indexOf(a.id);
    const indexB = plansToFetch.indexOf(b.id);
    return indexA - indexB;
  });

  React.useEffect(() => {
    if (isSuccess && sortedSubscriptions.length > 0) {
      const isCurrentPlanAvailable = sortedSubscriptions.some(
        (plan) => plan.id === selectedPlanId,
      );

      if (!isCurrentPlanAvailable) {
        const defaultPlanAvailable = sortedSubscriptions.some(
          (plan) => plan.id === defaultPlan,
        );

        if (defaultPlanAvailable) {
          setSelectedPlanId(defaultPlan);
        } else {
          setSelectedPlanId(sortedSubscriptions[0].id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, sortedSubscriptions.length, defaultPlan]);

  const selectedPlan = sortedSubscriptions.find(
    (plan) => plan.id === selectedPlanId,
  );
  const selectedCoinPlanId = useUserStore((store) => store.selectedCoinPlanId);
  const selectedCoinPlan = coins.find((coin) => coin.id === selectedCoinPlanId);

  const monthlyPlan = sortedSubscriptions.find((plan) =>
    MONTHLY_PLAN_IDS.includes(plan.id as (typeof MONTHLY_PLAN_IDS)[number]),
  );

  const checkIsPlanActive = (planId: string) => {
    return selectedPlanId === planId;
  };

  return {
    isLoadingPlans: isLoading,
    selectedPlan,
    selectedCoinPlan,
    stripePlans: sortedSubscriptions,
    monthlyPlan,
    checkIsPlanActive,
    isSuccess,
    billingPortalLink,
    coinsPlans: coins,
  };
};
